import React, {useCallback, useMemo} from 'react';
import styled from 'styled-components';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const SPACING_SM = '0.75rem';

const StyledAccordion = styled(Accordion)`
    &.taxonomy-dropdowns {
        width: 100%;
    }
`;

const CheckboxWrapper = styled.div`
    margin-left: ${SPACING_SM};
`;

const StyledCheckbox = styled.input`
    margin-right: ${SPACING_SM};
`;

const StyledLabel = styled.label`
    font-size: 0.875rem;
`;

/**
 * @typedef {Object} Manufacturer
 * @property {string} id - Unique identifier for the manufacturer
 * @property {string} nombrerazonsocial - Display name of the manufacturer
 */

/**
 * Determines if a manufacturer is selected.
 * @param {Set<number>} selectedIds - Currently selected manufacturer ids
 * @param {number} manufacturerId - Manufacturer ID to check
 * @returns {boolean}
 */
const isManufacturerSelected = (selectedIds, manufacturerId) => {
    return selectedIds.has(manufacturerId);
};

/**
 * Returns a new Set based on checkbox state.
 * @param {Set<number>} selectedIds - Current selected manufacturer ids
 * @param {number} manufacturerId - Manufacturer id being toggled
 * @param {boolean} isChecked - New checked state
 * @returns {Set<number>}
 */
const getUpdatedSelection = (selectedIds, manufacturerId, isChecked) => {
    const updated = new Set(selectedIds);
    if (isChecked) {
        updated.add(manufacturerId);
    } else {
        updated.delete(manufacturerId);
    }
    return updated;
};

/**
 * ManufacturerFilterUI Component
 * @param {{
 *   options: Manufacturer[],
 *   value: Set<number>,
 *   onChange: (updatedSelection: Set<number>) => void
 * }} props
 * @returns {JSX.Element}
 */
export const ManufacturerFilterUI = ({ options, value, onChange }) => {
    const selectedManufacturerIds = useMemo(() => value || new Set(), [value]);

    const handleCheckboxChange = useCallback(
        (event) => {
            const manufacturerId = Number(event.target.value);
            const isChecked = event.target.checked;

            const updated = getUpdatedSelection(
                selectedManufacturerIds,
                manufacturerId,
                isChecked
            );
            onChange(updated);
        },
        [selectedManufacturerIds]
    );

    return (
        <StyledAccordion className="taxonomy-dropdowns">
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="manufacturer-filter-content"
            >
                <span>Fabricantes</span>
            </AccordionSummary>
            <AccordionDetails>
                {options.map((manufacturer) => (
                    <CheckboxWrapper key={manufacturer.id} className="form-check">
                        <StyledCheckbox
                            type="checkbox"
                            className="form-check-input"
                            value={manufacturer.id}
                            checked={isManufacturerSelected(selectedManufacturerIds, manufacturer.id)}
                            onChange={handleCheckboxChange}
                        />
                        <StyledLabel className="form-check-label">
                            <small>{manufacturer.nombrerazonsocial}</small>
                        </StyledLabel>
                    </CheckboxWrapper>
                ))}
            </AccordionDetails>
        </StyledAccordion>
    );
};
